import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Button from '~components/ui/Button'
import ButtonLink from '~components/ui/ButtonLink'
import axios from 'axios'
import RoundedIcon from '~components/ui/RoundedIcon'
import Sprite from '~components/Sprite'
import Link from '../../ui/Link'
import Message from '~components/ui/Message'
import Select from '~components/ui/Select'
import { pageView } from '~helpers/ga'
import { flattenDeep } from 'lodash/array'

const Register = () => {
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated)
  const navigate = useNavigate()
  const copyrightDate = new Date().getFullYear()

  const [formData, setFormData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    telephone: '',
    country: 'AU',
    account_number: '',
    terms: false
  })

  const [message, setMessage] = useState({ type: '', text: '' })
  const [registered, setRegistered] = useState(false)
  const [countries, setCountries] = useState([])
  const [bottomLinks, setBottomLinks] = useState([])

  useEffect(() => {
    pageView()

    axios.get('/options', {
      params: {
        option: 'footer'
      }
    }).then(response => {
      setBottomLinks(response.data.fields.bottom_links)
    })

    document.body.classList.add('login-body')

    const cancelToken = axios.CancelToken.source()

    axios.get('/api/config/countries', { cancelToken: cancelToken.token }).then(response => {
      setCountries(response.data)
    })

    return () => {
      if (cancelToken) {
        cancelToken.cancel()
      }
    }
  }, [])

  const handleInputChange = event => {
    const target = event.target
    const name = target.name
    let value = target.value

    if (target.type === 'checkbox') {
      value = target.checked
    }

    setFormData({
      ...formData,
      [name]: value
    })
  }

  const handleCountryChange = e => {
    setFormData({
      ...formData,
      country: e.target.value
    })
  }

  const handleSubmit = () => {
    if (formData.busy) {
      return
    }

    setFormData({ ...formData, busy: true })

    axios.post('/api/account/register', formData).then(response => {
      setRegistered(true)
    })
      .catch(exception => {
        if (exception.response.status === 422) {
          const error = flattenDeep(Object.values(exception.response.data.errors))
          setMessage({ type: 'error', text: error })
          return
        }

        setMessage({ type: 'error', text: 'An unknown error occurred' })
      })
      .finally(() => {
        setFormData({ ...formData, busy: false })
      })
  }

  if (isAuthenticated) {
    navigate('/')
    return null
  }

  const countryOptions = countries.reduce((carry, country) => {
    carry.push({
      value: country.id,
      name: country.label
    })
    return carry
  }, [])

  return (
    <>
      {message.text && (
        <Message
          messageType={message.type}
          messageText={message.text}
        />
      )}
      <div className="mx-auto xxl:container md:px-8 xxl:px-0">
        <div className="w-full px-4 py-8 mx-auto md:p-10 lg:w-2/3 xl:w-1/2 xl:p-20 bg-lightGrey">
          <a href="/" className="z-40 block w-17 h-10 md:w-22.5 md:h-13.5 transition-opacity duration-150 hover:opacity-75 focus:opacity-75 ">
            <Sprite name="logo" />
          </a>

          {registered
            ? (
              <div className="mt-15">
                <h2 className="mt-4 text-left text-md">
                  Thank you for registering
                </h2>
                <p>
                  You will receive an email shortly with instructions on completing your account application. If you have any queries please contact&nbsp;
                  <a href="mailto:marketing@panache-lingerie.com">
                    marketing@panache-lingerie.com
                  </a>
                </p>

                <ButtonLink
                  href="/account/login"
                  theme="green-to-pink"
                  className="mt-10"
                >
                  Return
                </ButtonLink>
              </div>
              )
            : (
              <div className="mt-15">
                <p>
                  New account
                </p>

                <h2 className="mt-4 text-left text-md">
                  Apply for a Panache hub account
                </h2>

                <p className="mt-6">
                  Please complete the form to apply for access to The Panache Hub. We&apos;ll review your application and notify you by email when approved. If we require any further information we&apos;ll be in touch.
                </p>

                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center mt-6 hover:underline focus:underline"
                  aria-label="Read our terms &amp; conditions"
                >
                  Read our terms &amp; conditions

                  <span
                    className="flex-shrink-0 block w-4 h-4 ml-2 icon icon-fill"
                    aria-hidden
                  >
                    <Sprite name="external" />
                  </span>
                </a>

                <hr className="block w-full my-8 border border-white md:my-10" />

                <div className="flex-1 block px-4 py-3 mt-10 mb-5 rounded bg-grey max-w-screen-mdxl">
                  <p className="mb-6">
                    Please ensure the application is completed by someone with the authority to manage the organisation and its users, such as an owner / managing director. Additional users can be set up once the account is active.
                  </p>

                  <p>
                    <strong>
                      All fields are required. This information is used to verify your account and contact you if we require any additional information.
                    </strong>
                  </p>
                </div>

                <form className="mt-8 lg:mt-10" action="#" onSubmit={e => e.preventDefault() || handleSubmit()}>
                  <div className="w-full lg:w-2/3 xl:w-3/4">
                    <label className="block" htmlFor="company_name">
                      Company name
                    </label>

                    <input name="company_name" id="company_name" type="text" required value={formData.company_name} onChange={handleInputChange} className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10" placeholder="Company name" />
                  </div>

                  <div className="w-full mt-6 lg:mt-8 lg:w-2/3 xl:w-3/4">
                    <label className="block" htmlFor="first_name">
                      First name
                    </label>

                    <input name="first_name" id="first_name" type="text" required value={formData.first_name} onChange={handleInputChange} className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10" placeholder="First name" />
                  </div>

                  <div className="w-full mt-6 lg:mt-8 lg:w-2/3 xl:w-3/4">
                    <label className="block" htmlFor="last_name">
                      Last name
                    </label>

                    <input name="last_name" id="last_name" type="text" required value={formData.last_name} onChange={handleInputChange} className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10" placeholder="Last name" />
                  </div>

                  <div className="w-full mt-6 lg:mt-8 lg:w-2/3 xl:w-3/4">
                    <label className="block" htmlFor="email">
                      Email
                    </label>

                    <input name="email" id="email" type="email" required value={formData.email} onChange={handleInputChange} className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10 " placeholder="Email" />
                  </div>

                  <div className="w-full mt-6 lg:mt-8 lg:w-2/3 xl:w-3/4">
                    <label className="block" htmlFor="telephone">
                      Contact number
                    </label>

                    <input name="telephone" id="telephone" type="tel" required value={formData.telephone} onChange={handleInputChange} className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10" placeholder="Contact number" />
                  </div>

                  <div className="w-full mt-6 lg:mt-8 lg:w-2/3 xl:w-3/4">
                    <label className="block">
                      Country

                      <Select
                        name="country"
                        required
                        onChange={handleCountryChange}
                        className="w-full h-12 pl-4 pr-10 mt-2 transition-colors duration-150 ease-in-out bg-no-repeat rounded-md outline-none appearance-none cursor-pointer border-grey bg-chevron-down bg-right-4 bg-3 focus:border-charcoal hover:border-charcoal"
                        items={countryOptions}
                        value={formData.country}
                      />
                    </label>
                  </div>

                  <div className="w-full mt-6 lg:mt-8 lg:w-2/3 xl:w-3/4">
                    <label
                      className="block"
                      htmlFor="account_number"
                    >
                      Account number
                    </label>

                    <input name="account_number" id="account_number" type="text" required value={formData.account_number} onChange={handleInputChange} className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10" placeholder="Account number" />
                  </div>

                  <div className="flex-1 block px-4 py-3 mt-6 rounded lg:mt-8 bg-grey max-w-screen-mdxl">
                    <p>
                      You will find your account number on the top of a recent invoice or order confirmation. If you are having difficulty locating this please get in touch with your regular Panache contact.
                    </p>
                  </div>

                  <div className="flex items-center mt-6 lg:mt-8">
                    <input id="terms" name="terms" type="checkbox" required checked={formData.terms} onChange={handleInputChange} className="w-6 h-6 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />

                    <label htmlFor="terms" className="block ml-4">
                      <strong>
                        {'I agree to Panache\'s T&C\'s'}
                      </strong>
                    </label>
                  </div>

                  <div className="w-full mt-8 lg:mt-10 lg:w-2/3 xl:w-3/4">
                    <Button type="submit" disabled={formData.busy} className="relative flex-shrink-0 inline-block p-4 text-white transition-colors duration-200 ease-in-out border bg-green lg:pr-6 hover:border-white border-green hover:bg-lightGrey focus:border-white focus:bg-lightGrey rounded-0">
                      Submit application
                    </Button>
                  </div>
                </form>

                <div className="mt-8 lg:mt-10">
                  <div className="leading-5">
                    Already have an account? &nbsp;
                    <Link href="/account/login" className="inline-block underline hover:no-underline">
                      Log in now
                    </Link>
                  </div>
                </div>
              </div>
              )}
        </div>

        <div className="flex flex-wrap w-full mx-auto mt-10 lg:w-2/3 xl:w-1/2 md:flex-col-reverse md:border-coral md:border-t md:pt-4 lg:flex-row-reverse lg:flex-no-wrap lg:items-start">
          <a href="mailto:info@panache-lingerie.com" className="inline-flex items-center flex-grow-0 flex-shrink-0 mx-auto mb-10 group md:mt-4 md:mx-0 md:mb-0 lg:mt-0 lg:ml-4">
            <RoundedIcon name="label" className="inline-block mr-2 w-7 h-7" />
            Need help?
          </a>

          <ul className="flex-wrap w-full -mb-3 text-center md:w-auto md:inline-flex md:text-left md:-mb-1 lg:mr-auto">
            <li className="hidden mb-3 md:mb-1 md:mr-3 md:block">
              &copy; Panache
              {' '}
              {copyrightDate}
            </li>

            {bottomLinks.map(link => (
              <li key={link.key} className="block mb-3 md:mb-1 md:mr-3">
                <Link href={link.attributes.link} className="hover:underline">
                  {link.attributes.label}
                </Link>
              </li>
            ))}

            <li className="block mb-3 md:mb-1 md:mr-3 md:hidden">
              &copy; Panache
              {' '}
              {copyrightDate}
            </li>
          </ul>
        </div>
      </div>
    </>
  )
}

Register.propTypes = {
  isAuthenticated: PropTypes.bool
}

const mapStateToProps = state => {
  return {
    isAuthenticated: state.auth.isAuthenticated
  }
}

export default connect(mapStateToProps)(Register)
